/* Shopify Quick Add Drawer Component */

function formatMoney(cents) {
  if (cents == null) return '';
  const num = typeof cents === 'number' ? cents : parseInt(cents, 10);
  if (isNaN(num)) return '';
  return '$' + (num / 100).toFixed(2);
}

function normalizeProductOptions(product) {
  if (!product || !Array.isArray(product.options)) return [];
  return product.options.map((opt, idx) => {
    const optName = typeof opt === 'string' ? opt : (opt.name || ('Option ' + (idx + 1)));
    let optValues = [];
    if (opt && Array.isArray(opt.values) && opt.values.length > 0) {
      optValues = opt.values.map(v => typeof v === 'string' ? v : (v.name || String(v)));
    } else if (Array.isArray(product.variants)) {
      const set = new Set();
      product.variants.forEach(variant => {
        let val = null;
        if (Array.isArray(variant.options) && variant.options[idx] != null) {
          val = variant.options[idx];
        } else if (variant['option' + (idx + 1)] != null) {
          val = variant['option' + (idx + 1)];
        }
        if (val != null && String(val).trim() !== '') {
          set.add(String(val).trim());
        }
      });
      optValues = Array.from(set);
    }
    return {
      name: optName,
      position: idx + 1,
      values: optValues
    };
  });
}

function getVariantOption(variant, index) {
  if (!variant) return null;
  if (Array.isArray(variant.options) && variant.options[index] != null) {
    return String(variant.options[index]).trim();
  }
  const propVal = variant['option' + (index + 1)];
  if (propVal != null) {
    return String(propVal).trim();
  }
  return null;
}

class QuickAddDrawer extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.backdrop = this.querySelector('.quick-add-drawer__backdrop');
    this.content = this.querySelector('.quick-add-drawer__content');
    this.closeBtn = this.querySelector('.quick-add-drawer__close');
    this.productTitleEl = this.querySelector('[data-product-title]');
    this.productPriceEl = this.querySelector('[data-product-price]');
    this.optionsContainer = this.querySelector('[data-drawer-options]');
    this.variantIdInput = this.querySelector('[data-selected-variant-id]');
    this.addBtn = this.querySelector('[data-add-to-bag]');
    this.btnText = this.querySelector('[data-btn-text]');
    this.btnSpinner = this.querySelector('[data-btn-spinner]');
    this.errorEl = this.querySelector('[data-drawer-error]');
    this.detailsLink = this.querySelector('[data-full-details-link]');

    this.isOpen = false;
    this.currentProduct = null;
    this.selectedOptions = {};
    this.lastTrigger = null;

    this.addEventListener('click', event => {
      if (event.target.closest('[data-drawer-close]')) {
        this.close();
      }
    }, { signal });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    }, { signal });

    if (this.addBtn) {
      this.addBtn.addEventListener('click', () => {
        this.handleAddToCart();
      }, { signal });
    }

    // Global listener for card swatches and quick add buttons
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-quick-add-trigger]');
      if (trigger) {
        event.preventDefault();
        const card = trigger.closest('[data-product-card]');
        if (card) this.openWithCard(card, trigger);
        return;
      }

      const swatch = event.target.closest('.product-card__swatch');
      if (swatch) {
        event.preventDefault();
        const card = swatch.closest('[data-product-card]');
        if (!card) return;
        const colorName = swatch.dataset.colorName;
        const swatchImg = swatch.dataset.swatchImage;
        const variantPrice = swatch.dataset.variantPrice;
        const variantCompare = swatch.dataset.variantCompare;

        card.querySelectorAll('.product-card__swatch').forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        if (colorName) card.dataset.selectedColor = colorName;

        const primaryImg = card.querySelector('.product-card__img--primary');
        if (primaryImg && swatchImg) {
          primaryImg.src = swatchImg;
          primaryImg.removeAttribute('srcset');
        }

        const priceEl = card.querySelector('[data-card-price]');
        if (priceEl && variantPrice) {
          if (variantCompare) {
            priceEl.innerHTML = '<span class="price--sale">' + variantPrice + '</span> <s class="price--compare">' + variantCompare + '</s>';
          } else {
            priceEl.innerHTML = '<span class="price--regular">' + variantPrice + '</span>';
          }
        }

        const saleBadgeEl = card.querySelector('[data-badge-sale], .product-card__badge--sale');
        if (saleBadgeEl) {
          if (swatch.dataset.variantDiscount) {
            saleBadgeEl.textContent = swatch.dataset.variantDiscount;
            saleBadgeEl.style.display = '';
          } else if (!variantCompare) {
            saleBadgeEl.style.display = 'none';
          }
        }
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  openWithCard(card, trigger) {
    this.lastTrigger = trigger;
    const jsonScript = card.querySelector('[data-product-json]');
    let productData = null;
    if (jsonScript) {
      try {
        productData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }

    const preferredColor = card.dataset.selectedColor ||
      card.querySelector('.product-card__swatch.is-active')?.dataset?.colorName ||
      null;

    if (productData) {
      this.populateProduct(productData, preferredColor);
      this.open();
    } else if (card.dataset.productHandle) {
      this.fetchProduct(card.dataset.productHandle, preferredColor);
    }
  }

  async fetchProduct(handle, preferredColor) {
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();
      this.populateProduct(product, preferredColor);
      this.open();
    } catch (err) {
      console.error(err);
    }
  }

  populateProduct(product, preferredColor) {
    this.currentProduct = product;
    this.currentProduct.options = normalizeProductOptions(product);
    this.selectedOptions = {};
    if (this.errorEl) this.errorEl.textContent = '';
    if (this.btnSpinner) this.btnSpinner.hidden = true;
    if (this.btnText) this.btnText.hidden = false;

    // Title: vendor + title or just title if title includes vendor
    const vendor = product.vendor || 'Keds';
    const displayTitle = product.title.toLowerCase().startsWith(vendor.toLowerCase())
      ? product.title
      : vendor + ' ' + product.title;
    this.productTitleEl.textContent = displayTitle;

    // Details Link
    const productUrl = product.url || ('/products/' + product.handle);
    this.detailsLink.href = productUrl;

    // Clear and build options
    this.optionsContainer.innerHTML = '';

    // If single variant or only default option
    const hasOnlyDefaultVariant = product.has_only_default_variant ||
      (this.currentProduct.options.length === 1 && /^(title|default)$/i.test(this.currentProduct.options[0].name));

    if (hasOnlyDefaultVariant || !this.currentProduct.variants || this.currentProduct.variants.length <= 1) {
      if (this.currentProduct.variants && this.currentProduct.variants.length > 0) {
        this.selectedVariant = this.currentProduct.variants[0];
        this.variantIdInput.value = this.selectedVariant.id;
        if (this.selectedVariant.available) {
          this.addBtn.disabled = false;
          this.btnText.textContent = 'Add to Bag';
        } else {
          this.addBtn.disabled = true;
          this.btnText.textContent = 'Sold out';
        }
        const priceToDisplay = this.selectedVariant.price;
        const compareToDisplay = this.selectedVariant.compare_at_price;
        if (compareToDisplay && compareToDisplay > priceToDisplay) {
          this.productPriceEl.innerHTML =
            '<span class="price--sale">' + formatMoney(priceToDisplay) + '</span> ' +
            '<s class="price--compare">' + formatMoney(compareToDisplay) + '</s>';
        } else {
          this.productPriceEl.innerHTML =
            '<span class="price--regular">' + formatMoney(priceToDisplay) + '</span>';
        }
      }
      return;
    }

    // Detect options
    const colorOpt = this.currentProduct.options.find(opt => /colou?r/i.test(opt.name));
    const sizeOpt = this.currentProduct.options.find(opt => /size/i.test(opt.name));
    const colorOptName = colorOpt ? colorOpt.name : null;
    const sizeOptName = sizeOpt ? sizeOpt.name : null;

    const colorIndex = colorOpt ? this.currentProduct.options.indexOf(colorOpt) : -1;
    const sizeIndex = sizeOpt ? this.currentProduct.options.indexOf(sizeOpt) : -1;

    // Other options (e.g. Width)
    const otherOpts = this.currentProduct.options.filter(opt =>
      opt !== colorOpt && opt !== sizeOpt && !/^(title|default)$/i.test(opt.name)
    );

    // Determine initial color
    const colorValues = colorOpt ? colorOpt.values : [];
    let activeColor = null;
    if (colorOpt && colorValues.length > 0) {
      if (preferredColor && colorValues.includes(preferredColor)) {
        activeColor = preferredColor;
      } else {
        const firstAvail = this.currentProduct.variants.find(v => v.available && colorIndex >= 0 && getVariantOption(v, colorIndex));
        activeColor = firstAvail ? getVariantOption(firstAvail, colorIndex) : colorValues[0];
      }
      this.selectedOptions[colorOptName] = activeColor;
    }

    // Set other non-size options default (e.g. Width)
    otherOpts.forEach(opt => {
      const optIdx = this.currentProduct.options.indexOf(opt);
      let initialVal = null;
      if (activeColor && colorIndex >= 0) {
        const availForColor = this.currentProduct.variants.find(v => v.available && getVariantOption(v, colorIndex) === activeColor);
        if (availForColor && optIdx >= 0) {
          initialVal = getVariantOption(availForColor, optIdx);
        }
      }
      if (!initialVal && opt.values.length > 0) {
        initialVal = opt.values[0];
      }
      if (initialVal) {
        this.selectedOptions[opt.name] = initialVal;
      }
    });

    // 1. Render Color Section (if color option exists)
    if (colorOptName && colorValues.length > 0) {
      const colorSection = document.createElement('div');
      colorSection.className = 'quick-add-drawer__option-group quick-add-drawer__option-group--color';

      const colorHeader = document.createElement('div');
      colorHeader.className = 'quick-add-drawer__option-header';

      const colorLabel = document.createElement('span');
      colorLabel.className = 'quick-add-drawer__label';
      colorLabel.innerHTML = 'Color <strong data-selected-color-label>' + (activeColor || '') + '</strong>';

      const colorCount = document.createElement('span');
      colorCount.className = 'quick-add-drawer__count-label';
      colorCount.setAttribute('data-color-count-label', '');
      const activeIdx = activeColor ? colorValues.indexOf(activeColor) : 0;
      colorCount.textContent = (activeIdx >= 0 ? activeIdx + 1 : 1) + ' of ' + colorValues.length;

      colorHeader.appendChild(colorLabel);
      colorHeader.appendChild(colorCount);
      colorSection.appendChild(colorHeader);

      const swatchesGrid = document.createElement('div');
      swatchesGrid.className = 'quick-add-drawer__swatches-grid';

      colorValues.forEach((val, idx) => {
        const swatchBtn = document.createElement('button');
        swatchBtn.type = 'button';
        swatchBtn.className = 'quick-add-drawer__color-swatch' + (val === activeColor ? ' is-selected' : '');
        swatchBtn.setAttribute('data-color-value', val);
        swatchBtn.setAttribute('data-color-index', idx);
        swatchBtn.setAttribute('aria-label', val);

        // Find variant for image preview
        const variantForColor = this.currentProduct.variants.find(v => getVariantOption(v, colorIndex) === val);
        const imgUrl = variantForColor?.featured_image?.src ||
          (typeof variantForColor?.featured_image === 'string' ? variantForColor.featured_image : null) ||
          variantForColor?.featured_media?.preview_image?.src || null;

        if (imgUrl) {
          const img = document.createElement('img');
          img.src = imgUrl;
          img.alt = val;
          img.loading = 'lazy';
          swatchBtn.appendChild(img);
        } else {
          const colorDot = document.createElement('span');
          colorDot.className = 'quick-add-drawer__swatch-dot color-' + val.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          swatchBtn.appendChild(colorDot);
        }

        swatchBtn.addEventListener('click', () => {
          this.handleColorChange(val, idx, colorValues.length);
        });

        swatchesGrid.appendChild(swatchBtn);
      });

      colorSection.appendChild(swatchesGrid);
      this.optionsContainer.appendChild(colorSection);
    }

    // 2. Render Size Section (if size exists)
    if (sizeOptName) {
      const sizeSection = document.createElement('div');
      sizeSection.className = 'quick-add-drawer__option-group quick-add-drawer__option-group--size';

      const sizeLabel = document.createElement('label');
      sizeLabel.className = 'quick-add-drawer__label';
      sizeLabel.htmlFor = 'QuickAddSelectSize';
      sizeLabel.textContent = sizeOpt.name || 'Size';
      sizeSection.appendChild(sizeLabel);

      const selectWrap = document.createElement('div');
      selectWrap.className = 'quick-add-drawer__select-wrap';

      const select = document.createElement('select');
      select.id = 'QuickAddSelectSize';
      select.className = 'quick-add-drawer__select';
      select.setAttribute('data-size-select', '');

      select.addEventListener('change', () => {
        this.selectedOptions[sizeOptName] = select.value;
        this.updateVariantState();
      });

      selectWrap.appendChild(select);

      const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrowSvg.setAttribute('class', 'select-arrow');
      arrowSvg.setAttribute('width', '10');
      arrowSvg.setAttribute('height', '6');
      arrowSvg.setAttribute('viewBox', '0 0 10 6');
      arrowSvg.setAttribute('fill', 'none');
      arrowSvg.setAttribute('stroke', 'currentColor');
      arrowSvg.setAttribute('stroke-width', '1.6');
      arrowSvg.innerHTML = '<path d="M1 1l4 4 4-4" stroke-linecap="round" stroke-linejoin="round"/>';
      selectWrap.appendChild(arrowSvg);

      sizeSection.appendChild(selectWrap);
      this.optionsContainer.appendChild(sizeSection);

      this.populateSizesForColor(activeColor);
    }

    // 3. Render any additional options (e.g. Width)
    otherOpts.forEach(opt => {
      const otherSection = document.createElement('div');
      otherSection.className = 'quick-add-drawer__option-group';

      const label = document.createElement('label');
      label.className = 'quick-add-drawer__label';
      label.textContent = opt.name;
      otherSection.appendChild(label);

      const selectWrap = document.createElement('div');
      selectWrap.className = 'quick-add-drawer__select-wrap';

      const select = document.createElement('select');
      select.className = 'quick-add-drawer__select';
      select.setAttribute('aria-label', opt.name);

      opt.values.forEach(val => {
        const optElem = document.createElement('option');
        optElem.value = val;
        optElem.textContent = val;
        if (val === this.selectedOptions[opt.name]) optElem.selected = true;
        select.appendChild(optElem);
      });

      select.addEventListener('change', () => {
        this.selectedOptions[opt.name] = select.value;
        this.populateSizesForColor(this.selectedOptions[colorOptName]);
        this.updateVariantState();
      });

      selectWrap.appendChild(select);

      const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrowSvg.setAttribute('class', 'select-arrow');
      arrowSvg.setAttribute('width', '10');
      arrowSvg.setAttribute('height', '6');
      arrowSvg.setAttribute('viewBox', '0 0 10 6');
      arrowSvg.setAttribute('fill', 'none');
      arrowSvg.setAttribute('stroke', 'currentColor');
      arrowSvg.setAttribute('stroke-width', '1.6');
      arrowSvg.innerHTML = '<path d="M1 1l4 4 4-4" stroke-linecap="round" stroke-linejoin="round"/>';
      selectWrap.appendChild(arrowSvg);

      otherSection.appendChild(selectWrap);
      this.optionsContainer.appendChild(otherSection);
    });

    this.updateVariantState();
  }

  handleColorChange(colorVal, index, total) {
    const colorOpt = this.currentProduct.options.find(opt => /colou?r/i.test(opt.name));
    const colorOptName = colorOpt ? colorOpt.name : 'Color';

    this.selectedOptions[colorOptName] = colorVal;

    // Update label
    const labelEl = this.querySelector('[data-selected-color-label]');
    if (labelEl) labelEl.textContent = colorVal;

    const countEl = this.querySelector('[data-color-count-label]');
    if (countEl) countEl.textContent = (index + 1) + ' of ' + total;

    // Update swatch selected styling
    this.querySelectorAll('.quick-add-drawer__color-swatch').forEach(btn => {
      btn.classList.toggle('is-selected', btn.dataset.colorValue === colorVal);
    });

    // Refresh size availability for this color
    this.populateSizesForColor(colorVal);
    this.updateVariantState();
  }

  populateSizesForColor(colorVal) {
    const sizeSelect = this.querySelector('[data-size-select]');
    if (!sizeSelect || !this.currentProduct) return;

    const colorOpt = this.currentProduct.options.find(opt => /colou?r/i.test(opt.name));
    const sizeOpt = this.currentProduct.options.find(opt => /size/i.test(opt.name));
    if (!sizeOpt) return;

    const colorOptName = colorOpt ? colorOpt.name : null;
    const sizeOptName = sizeOpt.name;

    const colorIdx = colorOpt ? this.currentProduct.options.indexOf(colorOpt) : -1;
    const sizeIdx = this.currentProduct.options.indexOf(sizeOpt);

    const prevSelectedSize = this.selectedOptions[sizeOptName];

    sizeSelect.innerHTML = '<option value="" disabled selected>Select Size</option>';

    let sizeMatched = false;

    sizeOpt.values.forEach(sizeVal => {
      // Find variant with this size, current color, and other current options (e.g. Width)
      const variant = this.currentProduct.variants.find(v => {
        const matchColor = colorIdx === -1 || getVariantOption(v, colorIdx) === colorVal;
        const matchSize = getVariantOption(v, sizeIdx) === sizeVal;
        const matchOther = Object.entries(this.selectedOptions).every(([optName, val]) => {
          if (optName === colorOptName || optName === sizeOptName) return true;
          const kIdx = this.currentProduct.options.findIndex(o => o.name === optName);
          return kIdx === -1 || getVariantOption(v, kIdx) === val;
        });
        return matchColor && matchSize && matchOther;
      });

      const opt = document.createElement('option');
      opt.value = sizeVal;

      if (!variant) {
        opt.textContent = sizeVal + ' (Unavailable)';
        opt.disabled = true;
      } else if (!variant.available) {
        opt.textContent = sizeVal + ' (Sold out)';
        opt.disabled = true;
      } else {
        opt.textContent = sizeVal;
        if (sizeVal === prevSelectedSize) {
          opt.selected = true;
          sizeMatched = true;
        }
      }

      sizeSelect.appendChild(opt);
    });

    if (!sizeMatched) {
      delete this.selectedOptions[sizeOptName];
      sizeSelect.value = '';
    }
  }

  updateVariantState() {
    if (!this.currentProduct) return;

    const sizeOpt = this.currentProduct.options.find(opt => /size/i.test(opt.name));
    const sizeOptName = sizeOpt ? sizeOpt.name : null;
    const hasSizeSelected = !sizeOptName || Boolean(this.selectedOptions[sizeOptName]);

    // Find variant matching current options
    let matchingVariant = null;

    if (this.currentProduct.variants.length === 1) {
      matchingVariant = this.currentProduct.variants[0];
    } else {
      matchingVariant = this.currentProduct.variants.find(v => {
        return this.currentProduct.options.every((opt, idx) => {
          if (/^(title|default)$/i.test(opt.name)) return true;
          const selectedVal = this.selectedOptions[opt.name];
          if (!selectedVal) return false;
          return getVariantOption(v, idx) === selectedVal;
        });
      });
    }

    // Update Price display
    const priceToDisplay = matchingVariant ? matchingVariant.price : this.currentProduct.price;
    const compareToDisplay = matchingVariant ? matchingVariant.compare_at_price : this.currentProduct.compare_at_price;

    if (compareToDisplay && compareToDisplay > priceToDisplay) {
      this.productPriceEl.innerHTML =
        '<span class="price--sale">' + formatMoney(priceToDisplay) + '</span> ' +
        '<s class="price--compare">' + formatMoney(compareToDisplay) + '</s>';
    } else {
      this.productPriceEl.innerHTML =
        '<span class="price--regular">' + formatMoney(priceToDisplay) + '</span>';
    }

    // Update Variant ID input and Button state
    if (matchingVariant) {
      this.variantIdInput.value = matchingVariant.id;
      if (matchingVariant.available) {
        this.addBtn.disabled = false;
        this.btnText.textContent = 'Add to Bag';
      } else {
        this.variantIdInput.value = '';
        this.addBtn.disabled = true;
        this.btnText.textContent = 'Sold out';
      }
    } else {
      this.variantIdInput.value = '';
      this.addBtn.disabled = true;
      this.btnText.textContent = hasSizeSelected ? 'Unavailable' : 'Select Size';
    }

    // Update full details URL with variant if available
    const baseUrl = this.currentProduct.url || ('/products/' + this.currentProduct.handle);
    this.detailsLink.href = matchingVariant ? (baseUrl + '?variant=' + matchingVariant.id) : baseUrl;
  }

  async handleAddToCart() {
    const variantId = this.variantIdInput.value;
    if (!variantId || this.addBtn.disabled) return;

    this.addBtn.disabled = true;
    this.btnText.hidden = true;
    this.btnSpinner.hidden = false;
    if (this.errorEl) this.errorEl.textContent = '';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not add to bag');
      }

      // Success: redirect directly to checkout
      this.btnSpinner.hidden = true;
      this.btnText.hidden = false;
      this.btnText.textContent = 'Redirecting to Checkout...';

      window.location.href = '/checkout';

    } catch (err) {
      this.btnSpinner.hidden = true;
      this.btnText.hidden = false;
      this.btnText.textContent = 'Add to Bag';
      this.addBtn.disabled = false;
      if (this.errorEl) {
        this.errorEl.textContent = err.message;
      }
    }
  }

  async updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      const count = cart.item_count;

      const bagLink = document.querySelector('a[href$="/cart"]');
      if (bagLink) {
        let badge = bagLink.querySelector('.bag-count');
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'bag-count';
            bagLink.appendChild(badge);
          }
          badge.textContent = count;
          bagLink.setAttribute('aria-label', 'Bag, ' + count + ' items');
        } else if (badge) {
          badge.remove();
          bagLink.setAttribute('aria-label', 'Bag, 0 items');
        }
      }
    } catch (e) {
      console.error('Error fetching updated cart:', e);
    }
  }

  open() {
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.body.classList.add('drawer-open');
    if (this.closeBtn) this.closeBtn.focus();
  }

  close() {
    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    if (this.lastTrigger) {
      this.lastTrigger.focus();
      this.lastTrigger = null;
    }
  }
}

if (!customElements.get('quick-add-drawer')) {
  customElements.define('quick-add-drawer', QuickAddDrawer);
}


/* KedsCollection Component: Section Rendering AJAX Filtering & Mobile Dialog */

class KedsCollection extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.sectionId = this.dataset.section;
    this.dialog = this.querySelector('[data-filter-dialog]');
    this.home = this.querySelector('[data-filter-home]');
    this.panel = this.querySelector('[data-filter-panel]');
    this.form = this.panel ? this.panel.querySelector('form') : null;
    this.opener = this.querySelector('[data-filter-open]');
    this.slot = this.querySelector('[data-filter-slot]');

    if (this.opener) this.opener.hidden = false;
    this.classList.add('has-drawer');

    // Mobile Dialog open/close
    this.addEventListener('click', event => {
      if (event.target.closest('[data-filter-open]')) {
        if (this.slot && this.panel) this.slot.append(this.panel);
        if (this.dialog) this.dialog.showModal();
        if (this.opener) this.opener.setAttribute('aria-expanded', 'true');
        document.body.classList.add('drawer-open');
      }

      if (event.target.closest('[data-filter-close]') || event.target === this.dialog) {
        if (this.dialog && this.dialog.open) this.dialog.close();
      }
    }, { signal });

    if (this.dialog) {
      this.dialog.addEventListener('close', () => {
        if (this.home && this.panel) this.home.append(this.panel);
        if (this.opener) {
          this.opener.setAttribute('aria-expanded', 'false');
          this.opener.focus();
        }
        document.body.classList.remove('drawer-open');
      }, { signal });
    }

    matchMedia('(min-width:960px)').addEventListener('change', event => {
      if (event.matches && this.dialog?.open) this.dialog.close();
    }, { signal });

    // Desktop Sort change
    const desktopSort = this.querySelector('[data-desktop-sort]');
    if (desktopSort) {
      desktopSort.addEventListener('change', event => {
        const sortVal = event.target.value;
        const hiddenSort = this.querySelector('[data-hidden-sort]');
        if (hiddenSort) hiddenSort.value = sortVal;
        const mobileSort = this.querySelector('[data-filter-sort]');
        if (mobileSort) mobileSort.value = sortVal;
        this.applyFilters(this.buildUrlWithSort(sortVal));
      }, { signal });
    }

    // Form change on desktop -> instant AJAX filtering
    if (this.form) {
      this.form.addEventListener('change', event => {
        // If change comes from mobile dialog sort, update hidden sort
        if (event.target.matches('[data-filter-sort]')) {
          const hiddenSort = this.querySelector('[data-hidden-sort]');
          if (hiddenSort) hiddenSort.value = event.target.value;
          const dtSort = this.querySelector('[data-desktop-sort]');
          if (dtSort) dtSort.value = event.target.value;
        }

        // On desktop, filter immediately!
        if (window.matchMedia('(min-width: 960px)').matches) {
          this.submitFormAjax();
        }
      }, { signal });

      // Form submit (e.g. mobile "See Results" button)
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.dialog?.open) this.dialog.close();
        this.submitFormAjax();
      }, { signal });
    }

    // Intercept clicks on Clear filters, active filter remove, and pagination
    this.addEventListener('click', event => {
      const clearBtn = event.target.closest('[data-filter-clear-all]');
      if (clearBtn) {
        event.preventDefault();
        if (this.dialog?.open) this.dialog.close();
        this.applyFilters(clearBtn.href || window.location.pathname);
        return;
      }

      const removeFilterLink = event.target.closest('[data-filter-remove]');
      if (removeFilterLink) {
        event.preventDefault();
        this.applyFilters(removeFilterLink.href);
        return;
      }

      const paginationLink = event.target.closest('[data-pagination-link]');
      if (paginationLink) {
        event.preventDefault();
        this.applyFilters(paginationLink.href, true);
        return;
      }
    }, { signal });

    // Handle browser back/forward buttons
    window.addEventListener('popstate', () => {
      this.applyFilters(window.location.href, false, false);
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.dialog?.open) {
      this.dialog.close();
      document.body.classList.remove('drawer-open');
    }
  }

  buildUrlWithSort(sortVal) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortVal);
    url.searchParams.delete('page');
    return url.toString();
  }

  submitFormAjax() {
    if (!this.form) return;
    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams();

    // Collect all checked tag values
    const checkedTags = Array.from(this.form.querySelectorAll('input[name="filter.tag"]:checked'))
      .map(el => el.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
      .filter(Boolean);

    for (const [key, val] of formData.entries()) {
      if (key !== 'filter.tag' && val !== '') {
        searchParams.append(key, val);
      }
    }

    let basePath = (this.form.getAttribute('action') || window.location.pathname).replace(/\/+$/, '');
    const collMatch = basePath.match(/^(\/collections\/[^\/]+)/);
    if (collMatch) {
      basePath = collMatch[1];
    }

    if (checkedTags.length > 0) {
      basePath += '/' + checkedTags.join('+');
    }

    const qs = searchParams.toString();
    const targetUrl = qs ? `${basePath}?${qs}` : basePath;
    this.applyFilters(targetUrl);
  }

  async applyFilters(url, scrollToGrid = false, updateHistory = true) {
    const gridContainer = this.querySelector('[data-grid-container]');
    if (gridContainer) gridContainer.classList.add('is-loading');

    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const res = await fetch(fetchUrl.toString());
      if (!res.ok) throw new Error('Failed to fetch filtered collection');
      const htmlText = await res.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');

      // 1. Replace main content (grid, pagination, count, active filters)
      const newGridContainer = doc.querySelector('[data-grid-container]');
      if (gridContainer && newGridContainer) {
        gridContainer.replaceWith(newGridContainer);
      }

      const countEl = this.querySelector('[data-collection-count]');
      const newCountEl = doc.querySelector('[data-collection-count]');
      if (countEl && newCountEl) {
        countEl.textContent = newCountEl.textContent;
      }

      const activeFiltersWrap = this.querySelector('[data-active-filters]');
      const newActiveFiltersWrap = doc.querySelector('[data-active-filters]');
      if (activeFiltersWrap && newActiveFiltersWrap) {
        activeFiltersWrap.replaceWith(newActiveFiltersWrap);
      }

      // 2. Update sidebar filter bodies to refresh counts and checkboxes
      const filterBody = this.querySelector('.collection-filters__body');
      const newFilterBody = doc.querySelector('.collection-filters__body');
      if (filterBody && newFilterBody) {
        // Preserve open details state across updates
        const openHandles = new Set();
        this.querySelectorAll('.collection-filter[open]').forEach(el => {
          const cls = [...el.classList].find(c => c.startsWith('collection-filter--'));
          if (cls) openHandles.add(cls);
        });

        filterBody.innerHTML = newFilterBody.innerHTML;

        openHandles.forEach(cls => {
          const detail = this.querySelector(`.${cls}`);
          if (detail) detail.open = true;
        });
      }

      // 3. Update Sort options
      const newDesktopSort = doc.querySelector('[data-desktop-sort]');
      const currentDesktopSort = this.querySelector('[data-desktop-sort]');
      if (newDesktopSort && currentDesktopSort) {
        currentDesktopSort.value = newDesktopSort.value;
      }

      // 4. Update History
      if (updateHistory) {
        const cleanUrl = new URL(url, window.location.origin);
        cleanUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', cleanUrl.toString());
      }

      if (scrollToGrid) {
        const topPos = this.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: Math.max(0, topPos), behavior: 'smooth' });
      }

    } catch (err) {
      console.error('Error applying filters via AJAX:', err);
      // Fallback: standard navigation
      window.location.href = url;
    }
  }
}

if (!customElements.get('keds-collection')) {
  customElements.define('keds-collection', KedsCollection);
}
